// ClassificationGuide — what the two classification axes actually mean, in one
// place, mountable anywhere an item gets minted.
//
// ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
// Migration 016 made material_type and commodity_class NOT NULL with no default,
// because a default is a classification nobody made — that is how 33 catalog rows
// ended up filed as 'component'. Every mint surface therefore stops and asks two
// questions a person has to answer.
//
// Which is correct, and which only works if they know the answers. The one-line
// hint under each select says roughly what a bucket is; it does not settle the
// question people actually stall on, which is always at a BOUNDARY. Bare board or
// assembled board. Component or electromechanical. Mechanical or hardware. A
// reviewer who cannot tell picks the first plausible option, and a wrong
// classification looks exactly like a right one forever after.
//
// So the definitions live here, with examples and the neighbor each value gets
// confused with, and this component goes wherever the question is asked.
//
// ── ONE DEFINITION AT A TIME ────────────────────────────────────────────────
// Each axis renders as a row of chips and shows the definition of whichever is
// open. The first version stacked all fifteen definitions, which is thorough and
// unusable: somebody stuck on one select does not want fifteen paragraphs, they
// want the two or three they are choosing between. Chips are also the honest
// shape for the task, because the question IS "which of these".
//
// ── ONE SOURCE, NOT A SECOND COPY ───────────────────────────────────────────
// Every word rendered here comes from classificationDisplay.tsx, the module the
// pickers, chips and badges already read. Nothing is retyped: a guide that
// restates the vocabulary in its own words is a guide that will disagree with the
// select next to it within a release. The `examples` and `notThis` fields on
// OptionMeta are REQUIRED, so a classification value added later cannot compile
// until somebody has written its definition.

import React from 'react';
import HelpDrawer from '../components/HelpDrawer';
import { Chip, ChipGroup } from '../components/ui/Chip';
import FadeIn from '../components/ui/FadeIn';
import Panel from '../components/ui/Panel';
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_META,
  COMMODITY_CLASSES,
  COMMODITY_CLASS_META,
  type OptionMeta,
} from './classificationDisplay';

const LINK_CLASS =
  'text-[11px] text-on-surface-variant underline hover:text-on-surface py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded';

/**
 * One axis: its values as chips, and the definition of whichever is open.
 *
 * ── WHY NOT ALL FIFTEEN AT ONCE ─────────────────────────────────────────────
 * This started as two stacked lists of every definition, which is thorough and
 * unusable: somebody stuck on one select does not want fifteen paragraphs, they
 * want the two or three they are choosing between (Mannie, 2026-08-03). Chips
 * are also the honest shape for the task — the question IS "which of these", and
 * a row of options reads as a set of choices rather than as documentation.
 *
 * ONE OPEN PER AXIS, not one across the whole panel. The two axes are separate
 * questions and people genuinely compare across them ("it is a raw material, but
 * is it mechanical or hardware?"). Forcing a single global selection would close
 * the answer you just read to show you the next one.
 *
 * Clicking the open chip closes it, so the panel can return to nothing.
 */
const AxisChips: React.FC<{
  idPrefix: string;
  values: string[];
  meta: Record<string, OptionMeta>;
  ariaLabel: string;
}> = ({ idPrefix, values, meta, ariaLabel }) => {
  const [open, setOpen] = React.useState<string | null>(null);
  const panelId = `${idPrefix}-definition`;
  const shown = open ? meta[open] : null;

  return (
    <div className="space-y-2">
      <ChipGroup label={ariaLabel}>
        {values.map((value) => {
          const selected = open === value;
          return (
            <Chip
              key={value}
              pressed={selected}
              onClick={() => setOpen(selected ? null : value)}
              aria-controls={panelId}
            >
              {meta[value].short}
            </Chip>
          );
        })}
      </ChipGroup>

      {/* A live region, so a screen reader hears the definition when a chip is
          pressed rather than silently repainting. min-height holds the panel
          steady so clicking between chips does not shuffle the whole drawer. */}
      <Panel id={panelId} label={`${ariaLabel}, definition`} on="panel" padding="sm">
        <div aria-live="polite" className="min-h-[4.75rem]">
            <FadeIn key={open ?? 'none'}>
            {shown ? (
              <>
                <p className="text-on-surface font-medium text-[13px]">{shown.label}</p>
                <p className="text-on-surface-variant mt-0.5">{shown.hint}</p>
                <p className="text-on-surface text-xs mt-1.5">
                  <span className="text-on-surface-variant">Like: </span>
                  {shown.examples}
                </p>
                <p className="text-amber-300/80 text-xs mt-1">{shown.notThis}</p>
              </>
          ) : (
            <p className="text-on-surface-variant text-xs">Pick one to see what goes in it.</p>
          )}
          </FadeIn>
        </div>
      </Panel>
    </div>
  );
};

/** The panel contents, exported so a page can drop them into its own help. */
export const ClassificationGuideContent: React.FC = () => (
  <>
    <p>
      Every new item needs both answers, and neither is filled in for you. They are
      two different questions, so a screw and a finished drone can share one and
      differ on the other.
    </p>

    <h3 className="text-on-surface font-semibold pt-2">Build stage</h3>
    <p className="text-on-surface-variant text-xs">
      Where this sits in what we make. The codes in the definitions are the standard
      SAP ones, so they match what you will read in a vendor system or a textbook.
    </p>
    <AxisChips
      idPrefix="build-stage"
      values={MATERIAL_TYPES}
      meta={MATERIAL_TYPE_META}
      ariaLabel="Build stages"
    />

    <h3 className="text-on-surface font-semibold pt-3">Item type</h3>
    <p className="text-on-surface-variant text-xs">
      What kind of thing it is, which is also who we buy it from. This is the one
      the catalog filters and badges on, so it is worth getting right.
    </p>
    <AxisChips
      idPrefix="item-type"
      values={COMMODITY_CLASSES}
      meta={COMMODITY_CLASS_META}
      ariaLabel="Item types"
    />

    <p className="pt-2 text-on-surface-variant text-xs">
      Still unsure? Pick the closest and say so to whoever owns the catalog. Both
      answers can be corrected on the item later. What cannot be corrected is a
      guess nobody knows was a guess.
    </p>
  </>
);

/**
 * The reusable trigger. Drop it beside any pair of classification controls.
 *
 * Renders as link text by default rather than the "?" circle: inside a card, a
 * second unlabeled "?" a few pixels from the field it explains reads as
 * decoration. Pass `asCircle` on a page header, where the circle is the
 * established convention and the words would be noise.
 */
export const ClassificationGuideButton: React.FC<{
  asCircle?: boolean;
  label?: string;
  className?: string;
}> = ({ asCircle = false, label = 'What do these mean?', className }) => (
  <HelpDrawer
    title="Build stages and item types"
    label={asCircle ? 'Build stages and item types' : label}
    {...(asCircle ? {} : { trigger: label, triggerClassName: className || LINK_CLASS })}
  >
    <ClassificationGuideContent />
  </HelpDrawer>
);

export default ClassificationGuideButton;
